import { useEffect, useRef, useState } from 'react';
import type * as React from 'react';
import { useGarden } from '../../garden/GardenContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useSpaceHeld } from '../../hooks/useSpaceHeld';
import {
  MIN_ZOOM,
  MAX_ZOOM,
  WHEEL_ZOOM_SENSITIVITY,
  DRAG_THRESHOLD_PX,
  EDGE_INSERT_THRESHOLD,
} from '../../garden/constants';
import { boundingBoxOf, closestEdgeIndex } from '../../utils/geometry';
import PlacedElement from '../PlacedElement/PlacedElement';
import ShapeVertexHandle from './ShapeVertexHandle';
import './GardenCanvas.css';

// Padding (px) left around the garden shape when the view is fit to it on mount.
const FIT_MARGIN_PX = 60;

function clampZoom(value: number): number {
  return Math.min(Math.max(value, MIN_ZOOM), MAX_ZOOM);
}

function GardenCanvas() {
  const {
    elements,
    shape,
    armedType,
    isEditingShape,
    zoom,
    setZoom,
    addElement,
    clearArmedType,
    clearSelection,
    insertShapePoint,
  } = useGarden();
  const isMobile = useIsMobile();
  const isSpaceHeld = useSpaceHeld();

  const viewportRef = useRef<HTMLDivElement>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);

  // Refs mirroring the latest state so event handlers set up once (native
  // wheel listener) or that fire mid-gesture always read current values.
  const panRef = useRef(pan);
  panRef.current = pan;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const shapeRef = useRef(shape);
  shapeRef.current = shape;

  const panStartRef = useRef({ clientX: 0, clientY: 0, panX: 0, panY: 0 });
  const didPanRef = useRef(false);
  const pendingWheelZoomRef = useRef(false);
  const lastZoomRef = useRef(zoom);

  // Tracks every active touch pointer (by id) so a second finger touching
  // down can be detected and turned into a pinch-zoom gesture.
  const activeTouchesRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchStartRef = useRef<{
    distance: number;
    midX: number;
    midY: number;
    zoom: number;
    panX: number;
    panY: number;
  } | null>(null);

  // One-time fit-to-content on mount: center and scale the garden shape into view.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const box = boundingBoxOf(shapeRef.current);
    const contentWidth = Math.max(box.maxX - box.minX, 1);
    const contentHeight = Math.max(box.maxY - box.minY, 1);
    const fitZoom = clampZoom(
      Math.min(
        (rect.width - FIT_MARGIN_PX * 2) / contentWidth,
        (rect.height - FIT_MARGIN_PX * 2) / contentHeight,
      ),
    );
    const contentCenterX = (box.minX + box.maxX) / 2;
    const contentCenterY = (box.minY + box.maxY) / 2;

    lastZoomRef.current = fitZoom;
    setZoom(fitZoom);
    setPan({
      x: rect.width / 2 - contentCenterX * fitZoom,
      y: rect.height / 2 - contentCenterY * fitZoom,
    });
    // Fit is computed once from the shape present at mount; later shape edits
    // shouldn't yank the view around.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-anchor pan around the viewport center when zoom changes via the header
  // controls (zoomIn/zoomOut/resetZoom) rather than via cursor-anchored wheel zoom.
  useEffect(() => {
    if (zoom === lastZoomRef.current) return;
    if (pendingWheelZoomRef.current) {
      pendingWheelZoomRef.current = false;
      lastZoomRef.current = zoom;
      return;
    }
    const prevZoom = lastZoomRef.current;
    lastZoomRef.current = zoom;

    const viewport = viewportRef.current;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    setPan((p) => {
      const worldX = (cx - p.x) / prevZoom;
      const worldY = (cy - p.y) / prevZoom;
      return { x: cx - worldX * zoom, y: cy - worldY * zoom };
    });
  }, [zoom]);

  // Ctrl/Cmd+scroll (and trackpad pinch, which browsers report the same way)
  // zooms continuously, anchored under the cursor.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    function handleWheel(event: WheelEvent) {
      if (!(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();

      const rect = viewport!.getBoundingClientRect();
      const mx = event.clientX - rect.left;
      const my = event.clientY - rect.top;
      const currentZoom = zoomRef.current;
      const currentPan = panRef.current;
      const worldX = (mx - currentPan.x) / currentZoom;
      const worldY = (my - currentPan.y) / currentZoom;

      const factor = Math.exp(-event.deltaY * WHEEL_ZOOM_SENSITIVITY);
      const nextZoom = clampZoom(currentZoom * factor);

      pendingWheelZoomRef.current = true;
      setZoom(nextZoom);
      setPan({ x: mx - worldX * nextZoom, y: my - worldY * nextZoom });
    }

    viewport.addEventListener('wheel', handleWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', handleWheel);
  }, [setZoom]);

  function beginPan(event: React.PointerEvent<HTMLDivElement>) {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.setPointerCapture(event.pointerId);
    panStartRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
    };
    didPanRef.current = false;
    setIsPanning(true);
  }

  function viewportPoint(event: React.PointerEvent<HTMLDivElement>) {
    const rect = viewportRef.current!.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  // Two fingers down: start a pinch-zoom gesture anchored on their midpoint,
  // capturing the current zoom/pan so the gesture is measured from a fixed
  // baseline rather than accumulating drift move-by-move.
  function startPinch() {
    const points = Array.from(activeTouchesRef.current.values());
    if (points.length < 2) return;
    const [a, b] = points;
    pinchStartRef.current = {
      distance: Math.hypot(a.x - b.x, a.y - b.y),
      midX: (a.x + b.x) / 2,
      midY: (a.y + b.y) / 2,
      zoom: zoomRef.current,
      panX: panRef.current.x,
      panY: panRef.current.y,
    };
    setIsPanning(false);
    didPanRef.current = true;
  }

  function updatePinch() {
    const start = pinchStartRef.current;
    if (!start) return;
    const points = Array.from(activeTouchesRef.current.values());
    if (points.length < 2) return;
    const [a, b] = points;
    const distance = Math.hypot(a.x - b.x, a.y - b.y);
    const midX = (a.x + b.x) / 2;
    const midY = (a.y + b.y) / 2;

    const nextZoom = clampZoom(start.zoom * (distance / start.distance));
    const worldX = (start.midX - start.panX) / start.zoom;
    const worldY = (start.midY - start.panY) / start.zoom;

    pendingWheelZoomRef.current = true;
    setZoom(nextZoom);
    setPan({ x: midX - worldX * nextZoom, y: midY - worldY * nextZoom });
  }

  function handlePointerDownCapture(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === 'touch') {
      const viewport = viewportRef.current;
      if (viewport) {
        viewport.setPointerCapture(event.pointerId);
        activeTouchesRef.current.set(event.pointerId, viewportPoint(event));
      }
      // A second finger touching down starts a pinch, pre-empting any
      // element drag or single-finger pan — this runs in the capture phase
      // so it pre-empts PlacedElement's own drag handling.
      if (activeTouchesRef.current.size === 2) {
        event.stopPropagation();
        startPinch();
        return;
      }
      if (activeTouchesRef.current.size > 2) {
        event.stopPropagation();
        return;
      }
    }

    // Space always pans, even when starting on top of an element — this runs
    // in the capture phase so it pre-empts PlacedElement's own drag handling.
    if (isSpaceHeld && !isMobile) {
      event.stopPropagation();
      beginPan(event);
    }
  }

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    // On mobile there's no Space key: a plain drag pans, but only when it
    // starts on empty background so dragging an element still moves it.
    if (isMobile && event.target === viewportRef.current) {
      beginPan(event);
    }
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === 'touch' && activeTouchesRef.current.has(event.pointerId)) {
      activeTouchesRef.current.set(event.pointerId, viewportPoint(event));
    }
    if (pinchStartRef.current) {
      updatePinch();
      return;
    }

    if (!isPanning) return;
    const start = panStartRef.current;
    const dx = event.clientX - start.clientX;
    const dy = event.clientY - start.clientY;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) {
      didPanRef.current = true;
    }
    setPan({ x: start.panX + dx, y: start.panY + dy });
  }

  function handlePointerUp(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === 'touch') {
      activeTouchesRef.current.delete(event.pointerId);
      if (activeTouchesRef.current.size < 2) {
        pinchStartRef.current = null;
      }
      const viewport = viewportRef.current;
      if (viewport?.hasPointerCapture(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }
    }

    if (!isPanning) return;
    setIsPanning(false);
    const viewport = viewportRef.current;
    if (viewport?.hasPointerCapture(event.pointerId)) {
      viewport.releasePointerCapture(event.pointerId);
    }
  }

  function handleCanvasClick(event: React.MouseEvent<HTMLDivElement>) {
    if (didPanRef.current) {
      didPanRef.current = false;
      return;
    }
    // A click on a placed element or shape handle bubbles here as a native
    // 'click' event even though those children stopPropagation() on their
    // pointer events. Only react to clicks that land directly on the
    // viewport background itself (the world layer is pointer-events: none).
    if (event.target !== viewportRef.current) return;

    const rect = viewportRef.current.getBoundingClientRect();
    const x = (event.clientX - rect.left - pan.x) / zoom;
    const y = (event.clientY - rect.top - pan.y) / zoom;

    if (isEditingShape) {
      const { index, distance } = closestEdgeIndex(x, y, shape);
      if (distance <= EDGE_INSERT_THRESHOLD) {
        insertShapePoint(index, x, y);
      }
      return;
    }

    if (armedType) {
      addElement(armedType, x, y);
      clearArmedType();
      return;
    }

    if (!event.ctrlKey && !event.metaKey) {
      clearSelection();
    }
  }

  const polygonPoints = shape.map((p) => `${p.x},${p.y}`).join(' ');
  const viewportClassName = [
    'garden-canvas-viewport',
    armedType ? 'garden-canvas-viewport--armed' : '',
    isEditingShape ? 'garden-canvas-viewport--editing-shape' : '',
    isPanning
      ? 'garden-canvas-viewport--panning'
      : isSpaceHeld
        ? 'garden-canvas-viewport--pan-ready'
        : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    // This is a free-form visual placement surface (like a <canvas>), not a control; there is
    // no keyboard equivalent for "click at this pixel" to retrofit here.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={viewportRef}
      className={viewportClassName}
      onClick={handleCanvasClick}
      onPointerDownCapture={handlePointerDownCapture}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div
        className="garden-canvas"
        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
      >
        <svg className="garden-canvas__shape">
          <defs>
            <pattern id="garden-grid" width="25" height="25" patternUnits="userSpaceOnUse">
              <rect width="25" height="25" fill="#f7f9f4" />
              <path d="M 25 0 L 0 0 0 25" fill="none" stroke="#dbe7d4" strokeWidth="1" />
            </pattern>
          </defs>
          <polygon
            points={polygonPoints}
            fill="url(#garden-grid)"
            className="garden-canvas__polygon"
          />
        </svg>

        {elements.map((element) => (
          <PlacedElement key={element.id} element={element} />
        ))}

        {isEditingShape && shape.map((point) => <ShapeVertexHandle key={point.id} point={point} />)}
      </div>
    </div>
  );
}

export default GardenCanvas;
